import { FiPlusCircle } from 'react-icons/fi';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';

import { getUniqueKey } from 'utils/string';
import { getMonthsLaterDate } from 'utils/date';

import AddEmployeeStatusDetails from './AddEmployeeStatusDetails';
import { PROBATION, FIXED_TERM_CONTRACT } from 'constants/employeeStatus';

interface EmpStatusDetailItem {
  id?: number;
  keyId: number;
  category: string;
  date?: string;
}

type EmpStatusDetailStateAction = React.SetStateAction<EmpStatusDetailItem[]>;

export interface EmployeeStatusPayload {
  transitionDate: string;
  engagementStatus: {
    id: number;
    name: string;
  };
  empStatusDetails?: Array<{ id?: number; category: string; date?: string }>;
}

interface EmployeeStatusDetailsProps {
  employeeStatusPayload: EmployeeStatusPayload;
  setEmployeeStatusPayload: (updater: (payload: EmployeeStatusPayload) => EmployeeStatusPayload) => void;
  handleBlur: (event: { target: { name: string } }) => void;
  error: {
    empStatusDetails?: Array<{ category?: string | false; date?: string | false }>;
  };
  editIndex?: number;
}

export const getCheckIn = (statusDetails: EmpStatusDetailItem[] = []) => {
  return `Check-in ${statusDetails.filter((status) => status.category.includes('Check-in')).length + 1}`;
};

const EmployeeStatusDetails: React.FC<EmployeeStatusDetailsProps> = ({
  employeeStatusPayload,
  setEmployeeStatusPayload,
  handleBlur,
  error,
  editIndex,
}) => {
  const { transitionDate, engagementStatus } = employeeStatusPayload;
  const { name: statusName } = engagementStatus;

  const probationDefaults = useMemo<EmpStatusDetailItem[]>(
    () => [
      {
        keyId: getUniqueKey(),
        category: 'Mid Probation',
        date: getMonthsLaterDate(transitionDate, 3),
      },
      {
        keyId: getUniqueKey(),
        category: 'End Probation',
        date: getMonthsLaterDate(transitionDate, 6),
      },
    ],
    [transitionDate]
  );

  const ftcDefaults = useMemo<EmpStatusDetailItem[]>(() => [{ keyId: getUniqueKey(), category: getCheckIn() }], []);

  const initialEmpStatusDetails = useMemo<EmpStatusDetailItem[]>(() => {
    if (statusName === PROBATION) return probationDefaults;
    if (statusName === FIXED_TERM_CONTRACT) return ftcDefaults;
    return [];
  }, [statusName, probationDefaults, ftcDefaults]);

  const sourceKey = `${typeof editIndex === 'number' ? editIndex : 'new'}:` + `${statusName}:${transitionDate}`;

  const sourceEmpStatusDetails = useMemo<EmpStatusDetailItem[]>(() => {
    const existingEmpStatusDetails =
      employeeStatusPayload.empStatusDetails?.map((item) => ({
        ...item,
        keyId: item.id ?? getUniqueKey(),
      })) ?? [];

    const isValidForCurrentStatus = existingEmpStatusDetails.some((item) => {
      if (statusName === PROBATION) return item.category?.includes('Probation');
      if (statusName === FIXED_TERM_CONTRACT) return item.category?.includes('Check-in');
      return false;
    });

    if (
      [PROBATION, FIXED_TERM_CONTRACT].includes(statusName) &&
      (!isValidForCurrentStatus || existingEmpStatusDetails.length === 0)
    ) {
      return initialEmpStatusDetails;
    }

    return existingEmpStatusDetails;
  }, [statusName, employeeStatusPayload.empStatusDetails, initialEmpStatusDetails]);

  const [draftState, setDraftState] = useState(() => ({
    sourceKey,
    empStatusDetails: sourceEmpStatusDetails,
  }));

  const empStatusDetails = draftState.sourceKey === sourceKey ? draftState.empStatusDetails : sourceEmpStatusDetails;

  const setEmpStatusDetails = useCallback(
    (value: EmpStatusDetailStateAction) => {
      setDraftState((prev) => {
        const currentEmpStatusDetails = prev.sourceKey === sourceKey ? prev.empStatusDetails : sourceEmpStatusDetails;
        const nextEmpStatusDetails = typeof value === 'function' ? value(currentEmpStatusDetails) : value;
        return {
          sourceKey,
          empStatusDetails: nextEmpStatusDetails,
        };
      });
    },
    [sourceEmpStatusDetails, sourceKey]
  );

  const setEmployeeStatusPayloadRef = useRef(setEmployeeStatusPayload);
  useEffect(() => {
    setEmployeeStatusPayloadRef.current = setEmployeeStatusPayload;
  });

  useEffect(() => {
    setEmployeeStatusPayloadRef.current((payload) => {
      const newEmpStatusDetails = empStatusDetails.map(({ id, category, date }) => ({
        id,
        category,
        date,
      }));

      const currentEmpStatusDetails = Array.isArray(payload.empStatusDetails) ? payload.empStatusDetails : [];

      const areDetailsEqual =
        currentEmpStatusDetails.length === newEmpStatusDetails.length &&
        newEmpStatusDetails.every((next, index) => {
          const current = currentEmpStatusDetails[index];
          return current?.id === next.id && current?.category === next.category && current?.date === next.date;
        });

      if (areDetailsEqual) {
        return payload;
      }

      return { ...payload, empStatusDetails: newEmpStatusDetails };
    });
  }, [empStatusDetails]);

  const onAdd = () => {
    setEmpStatusDetails((prev) => [
      ...prev,
      {
        keyId: getUniqueKey(),
        category: statusName === FIXED_TERM_CONTRACT ? getCheckIn(prev) : '',
      },
    ]);
  };

  const handleDelete = (id: number) => {
    setEmpStatusDetails((prev) => prev.filter((item) => item.keyId !== id));
  };

  return (
    <div className="emp-status-details-form emp-status-details-form--add-leapfrogger custom-scroll-bar">
      <div className="emp-status-details-form__heading">Add {statusName} Category</div>

      <div className="emp-status-details-form__body">
        {empStatusDetails?.map((item, index) => (
          <AddEmployeeStatusDetails
            key={item.keyId}
            item={item}
            setEmpStatusDetails={setEmpStatusDetails}
            handleDelete={() => handleDelete(item.keyId)}
            handleBlur={handleBlur}
            error={error.empStatusDetails?.[index]}
            empStatusDetails={empStatusDetails}
            employeeStatusPayload={employeeStatusPayload}
            index={index}
            type={statusName}
            editIndex={editIndex}
          />
        ))}
      </div>

      <div className="emp-status-details-form__add-button" onClick={onAdd}>
        <FiPlusCircle className="emp-status-details-form__add-button--plus-icon" />
        <p className="emp-status-details-form__button-label">Add Another Category</p>
      </div>
    </div>
  );
};

export default EmployeeStatusDetails;
